import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
    "https://api.airporttransferstaxi.co.uk/api"
).replace(/\/+$/, "");

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

function clearAuthStorage() {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("userId");
  localStorage.removeItem("sessionId");
}

function redirectToSignIn() {
  const path = window.location.pathname;
  if (!path.startsWith("/signin")) {
    clearAuthStorage();
    window.location.href = "/signin";
  }
}

let isRefreshing = false;
type QueueItem = {
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
};
let failedQueue: QueueItem[] = [];

function processQueue(error: unknown | null, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token!);
  });
  failedQueue = [];
}

async function refreshAccessToken(): Promise<string> {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    throw new Error("No refresh token");
  }
  const { data } = await axios.post<{
    token: string;
    refreshToken?: string;
    userId?: string;
    sessionId?: string;
  }>(`${API_BASE_URL}/auth/refresh`, { refreshToken });
  localStorage.setItem("token", data.token);
  if (data.refreshToken) {
    localStorage.setItem("refreshToken", data.refreshToken);
  }
  if (data.userId) localStorage.setItem("userId", data.userId);
  if (data.sessionId) localStorage.setItem("sessionId", data.sessionId);
  return data.token;
}

function isAuthEndpoint(url?: string) {
  if (!url) return false;
  return url.includes("/auth/login") || url.includes("/auth/refresh");
}

function userIdFromJwt(token: string): string | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1])) as {
      userId?: string;
      sub?: string;
    };
    const id = payload.userId ?? payload.sub;
    return typeof id === "string" && /^[0-9a-f-]{36}$/i.test(id) ? id : null;
  } catch {
    return null;
  }
}

function resolveRequestUserId(token: string | null): string | null {
  const stored = localStorage.getItem("userId");
  if (stored && /^[0-9a-f-]{36}$/i.test(stored)) {
    return stored;
  }
  if (!token) return null;
  const fromJwt = userIdFromJwt(token);
  if (fromJwt) {
    localStorage.setItem("userId", fromJwt);
  }
  return fromJwt;
}

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const userId = resolveRequestUserId(token);
    if (userId) {
      config.headers["X-User-ID"] = userId;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (
      error.response?.status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      isAuthEndpoint(originalRequest.url)
    ) {
      if (
        error.response?.status === 401 &&
        !isAuthEndpoint(originalRequest?.url)
      ) {
        redirectToSignIn();
      }
      return Promise.reject(error);
    }

    if (!localStorage.getItem("refreshToken")) {
      redirectToSignIn();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const token = await refreshAccessToken();
      processQueue(null, token);
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      redirectToSignIn();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export { API_BASE_URL, clearAuthStorage };
export default axiosInstance;
